import { type FormEvent, type JSX, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '@/components/icons';
import { Badge, Button, Card, ConfirmModal, IconAction, Input, Skeleton } from '@/components/ui';
import {
  useCreateWebhook,
  useDeleteWebhook,
  useRetryDelivery,
  useTestWebhook,
  useUpdateWebhook,
  useWebhookDeliveries,
  useWebhooks,
  type CreateWebhookInput,
  type WebhookDelivery,
  type WebhookEndpoint,
  type WebhookEventType,
} from '@/hooks/useWebhooks';
import { ApiError } from '@/lib/api';
import { formatRelativeTime } from '@/lib/format';
import { cn } from '@/lib/utils';

const EVENT_TYPES: WebhookEventType[] = [
  'message.delivered',
  'message.failed',
  'inbound.received',
];

export default function WebhooksPage(): JSX.Element {
  const { t, i18n } = useTranslation('default');
  const lang = (i18n.resolvedLanguage ?? i18n.language ?? 'en').slice(0, 2);

  const list = useWebhooks();
  const createMut = useCreateWebhook();
  const deleteMut = useDeleteWebhook();
  const updateMut = useUpdateWebhook();
  const testMut = useTestWebhook();

  const [showCreate, setShowCreate] = useState(false);
  // Created secret is shown once after creation; null = no secret to show.
  const [justCreatedSecret, setJustCreatedSecret] = useState<{
    url: string;
    secret: string;
  } | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<WebhookEndpoint | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const items = list.data?.items ?? [];

  const onConfirmDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await deleteMut.mutateAsync(deleteConfirm.id);
    } finally {
      setDeleteConfirm(null);
    }
  };

  return (
    <div className="px-8 py-8 max-w-[1200px] mx-auto">
      <header className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="display text-[28px] font-semibold tracking-tight text-text leading-none m-0">
            {t('wh_title')}
          </h1>
          <p className="text-[13px] text-text-3 mt-1.5">{t('wh_sub')}</p>
        </div>
        <Button onClick={() => setShowCreate(true)}>
          <Icon.plus width="14" height="14" />
          {t('wh_new')}
        </Button>
      </header>

      {/* One-time secret display banner. Shown ONLY immediately after a
          successful create — re-mounting the page clears it. */}
      {justCreatedSecret && (
        <Card padding="md" className="mb-4 border-volt-500 bg-volt-50">
          <div className="flex items-start gap-3">
            <Icon.warn width="18" height="18" className="text-volt-700 flex-shrink-0 mt-px" />
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-semibold text-text mb-1">
                {t('wh_secret_save_now')}
              </p>
              <p className="text-[12px] text-text-3 mb-2">
                {t('wh_secret_save_desc', { url: justCreatedSecret.url })}
              </p>
              <code className="block w-full mono text-[12px] bg-surface border border-line rounded-sm px-2 py-1.5 break-all">
                {justCreatedSecret.secret}
              </code>
            </div>
            <button
              type="button"
              onClick={() => setJustCreatedSecret(null)}
              className="text-text-3 hover:text-text"
              aria-label="Dismiss"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M6 18 18 6M6 6l12 12" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </Card>
      )}

      {list.isLoading ? (
        <div className="space-y-3">
          {[0, 1].map((i) => (
            <Skeleton key={i} className="h-20 w-full rounded-md" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <Card padding="lg" className="text-center">
          <p className="text-[14px] text-text-2 font-medium">{t('wh_empty_title')}</p>
          <p className="text-[13px] text-text-3 mt-1">{t('wh_empty_sub')}</p>
          <Button className="mt-4" onClick={() => setShowCreate(true)}>
            <Icon.plus width="14" height="14" />
            {t('wh_new')}
          </Button>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((ep) => (
            <EndpointCard
              key={ep.id}
              endpoint={ep}
              lang={lang}
              expanded={expandedId === ep.id}
              testing={testMut.isPending && testMut.variables === ep.id}
              onToggleExpand={() => setExpandedId((prev) => (prev === ep.id ? null : ep.id))}
              onDelete={() => setDeleteConfirm(ep)}
              onTest={() => void testMut.mutateAsync(ep.id)}
              onToggleActive={() =>
                void updateMut.mutateAsync({ id: ep.id, input: { is_active: !ep.is_active } })
              }
            />
          ))}
        </div>
      )}

      {showCreate && (
        <CreateModal
          busy={createMut.isPending}
          onClose={() => setShowCreate(false)}
          onCreate={async (input) => {
            const res = await createMut.mutateAsync(input);
            setShowCreate(false);
            if (res.secret) {
              setJustCreatedSecret({ url: res.url, secret: res.secret });
            }
          }}
        />
      )}

      <ConfirmModal
        open={deleteConfirm !== null}
        title={t('wh_delete_title')}
        description={t('wh_delete_desc', { url: deleteConfirm?.url ?? '' })}
        confirmLabel={t('wh_delete_action')}
        cancelLabel={t('ac_cancel')}
        tone="danger"
        busy={deleteMut.isPending}
        onConfirm={() => void onConfirmDelete()}
        onClose={() => setDeleteConfirm(null)}
      />
    </div>
  );
}

interface EndpointCardProps {
  endpoint: WebhookEndpoint;
  lang: string;
  expanded: boolean;
  testing: boolean;
  onToggleExpand: () => void;
  onDelete: () => void;
  onTest: () => void;
  onToggleActive: () => void;
}

function EndpointCard({
  endpoint,
  lang,
  expanded,
  testing,
  onToggleExpand,
  onDelete,
  onTest,
  onToggleActive,
}: EndpointCardProps): JSX.Element {
  const { t } = useTranslation('default');
  return (
    <Card padding="none">
      <div className="px-5 py-4 flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant={endpoint.is_active ? 'success' : 'neutral'} className="text-[10px]">
              {endpoint.is_active ? t('wh_status_active') : t('wh_status_paused')}
            </Badge>
            {endpoint.description && (
              <span className="text-[13px] font-medium text-text">{endpoint.description}</span>
            )}
          </div>
          <div className="mono text-[12px] text-text-2 mt-1.5 break-all">{endpoint.url}</div>
          <div className="flex items-center gap-1.5 flex-wrap mt-2">
            {endpoint.events.map((ev) => (
              <span
                key={ev}
                className="mono text-[10px] uppercase tracking-[0.04em] font-semibold px-1.5 py-0.5 bg-surface-2 border border-line rounded-sm text-text-3"
              >
                {ev}
              </span>
            ))}
          </div>
          <div className="text-[11px] text-text-4 mt-2">
            {t('wh_created', { when: formatRelativeTime(endpoint.created_at, lang) })}
          </div>
        </div>
        <div className="flex items-start gap-0.5 flex-shrink-0">
          <IconAction
            icon={Icon.bell}
            label={endpoint.is_active ? t('wh_test') : t('wh_test_disabled_paused')}
            onClick={onTest}
            disabled={testing || !endpoint.is_active}
          />
          <IconAction
            icon={Icon.eye}
            label={expanded ? t('wh_hide_deliveries') : t('wh_view_deliveries')}
            onClick={onToggleExpand}
          />
          <IconAction
            icon={endpoint.is_active ? Icon.stop : Icon.play}
            label={endpoint.is_active ? t('wh_pause') : t('wh_resume')}
            onClick={onToggleActive}
            tone={endpoint.is_active ? 'warn' : 'primary'}
          />
          <IconAction
            icon={Icon.trash}
            label={t('wh_delete')}
            onClick={onDelete}
            tone="danger"
          />
        </div>
      </div>
      {expanded && <DeliveriesPanel endpointId={endpoint.id} lang={lang} />}
    </Card>
  );
}

function DeliveriesPanel({ endpointId, lang }: { endpointId: number; lang: string }): JSX.Element {
  const { t } = useTranslation('default');
  const deliveries = useWebhookDeliveries(endpointId, { limit: 25 });
  const retryMut = useRetryDelivery();

  if (deliveries.isLoading) {
    return (
      <div className="px-5 py-4 border-t border-line">
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }
  const items = deliveries.data?.items ?? [];
  if (items.length === 0) {
    return (
      <div className="px-5 py-4 border-t border-line text-[12px] text-text-3 text-center">
        {t('wh_deliveries_empty')}
      </div>
    );
  }

  return (
    <div className="border-t border-line bg-surface-2">
      <div className="px-5 py-3 text-[11px] font-medium text-text-4 uppercase tracking-[0.06em]">
        {t('wh_deliveries_title', { total: deliveries.data?.total ?? 0 })}
      </div>
      <table className="w-full text-[12px]">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-[0.06em] text-text-4 border-b border-line">
            <th className="px-5 py-2 font-medium">{t('wh_col_when')}</th>
            <th className="px-5 py-2 font-medium">{t('wh_col_event')}</th>
            <th className="px-5 py-2 font-medium">{t('wh_col_status')}</th>
            <th className="px-5 py-2 font-medium">{t('wh_col_response')}</th>
            <th className="px-5 py-2 font-medium"></th>
          </tr>
        </thead>
        <tbody>
          {items.map((d) => (
            <DeliveryRow
              key={d.id}
              delivery={d}
              lang={lang}
              retrying={retryMut.isPending && retryMut.variables?.deliveryId === d.id}
              onRetry={() =>
                void retryMut.mutateAsync({ endpointId, deliveryId: d.id })
              }
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface DeliveryRowProps {
  delivery: WebhookDelivery;
  lang: string;
  retrying: boolean;
  onRetry: () => void;
}

function DeliveryRow({ delivery: d, lang, retrying, onRetry }: DeliveryRowProps): JSX.Element {
  const { t } = useTranslation('default');
  // Three "logical" states the operator cares about:
  //   success                        — green
  //   failed-but-retrying            — yellow (next_retry_at set)
  //   failed-final                   — red   (gave up)
  // pending shouldn't linger long but render it as neutral if seen.
  const isRetrying = d.delivery_status === 'failed' && !!d.next_retry_at;
  const isTerminalFailed = d.delivery_status === 'failed' && !d.next_retry_at;
  let badgeVariant: 'success' | 'warn' | 'danger' | 'neutral' = 'neutral';
  let badgeLabel: string = d.delivery_status;
  if (d.delivery_status === 'success') {
    badgeVariant = 'success';
    badgeLabel = t('wh_status_success');
  } else if (isRetrying) {
    badgeVariant = 'warn';
    badgeLabel = t('wh_status_retrying');
  } else if (isTerminalFailed) {
    badgeVariant = 'danger';
    badgeLabel = t('wh_status_failed');
  }

  return (
    <tr className="border-b border-line-soft last:border-0">
      <td className="px-5 py-2 text-text-3 whitespace-nowrap">
        {formatRelativeTime(d.last_attempt_at ?? d.created_at, lang)}
      </td>
      <td className="px-5 py-2 mono text-text-2">{d.event_type}</td>
      <td className="px-5 py-2">
        <Badge variant={badgeVariant} className="text-[10px]">
          {badgeLabel}
        </Badge>
        {isRetrying && (
          <div className="text-[10px] text-text-4 mt-0.5">
            {t('wh_retry_at', {
              when: formatRelativeTime(d.next_retry_at ?? '', lang),
              attempts: d.attempts,
            })}
          </div>
        )}
        {d.attempts > 1 && !isRetrying && (
          <div className="text-[10px] text-text-4 mt-0.5">
            {t('wh_attempts', { count: d.attempts })}
          </div>
        )}
      </td>
      <td className="px-5 py-2 text-text-3">
        {d.response_status ? (
          <span className="num">{d.response_status}</span>
        ) : d.error_message ? (
          <span className="text-danger truncate inline-block max-w-[280px]" title={d.error_message}>
            {d.error_message}
          </span>
        ) : (
          '—'
        )}
      </td>
      <td className="px-5 py-2 text-right">
        {/* Retry is only useful for terminal-failed rows. Successful
            ones don't need it; auto-retrying ones already will. */}
        {isTerminalFailed && (
          <button
            type="button"
            onClick={onRetry}
            disabled={retrying}
            className="text-[11px] text-volt-500 font-medium hover:underline disabled:opacity-50"
          >
            {retrying ? t('wh_retrying') : t('wh_retry')}
          </button>
        )}
      </td>
    </tr>
  );
}

interface CreateModalProps {
  busy: boolean;
  onCreate: (input: CreateWebhookInput) => Promise<void>;
  onClose: () => void;
}

function CreateModal({ busy, onCreate, onClose }: CreateModalProps): JSX.Element {
  const { t } = useTranslation('default');
  const [url, setUrl] = useState('');
  const [description, setDescription] = useState('');
  const [events, setEvents] = useState<Set<WebhookEventType>>(
    new Set<WebhookEventType>(EVENT_TYPES),
  );
  const [error, setError] = useState<string | null>(null);

  const toggle = (ev: WebhookEventType) => {
    setEvents((prev) => {
      const next = new Set(prev);
      if (next.has(ev)) next.delete(ev);
      else next.add(ev);
      return next;
    });
  };

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    if (!url.trim()) {
      setError(t('wh_err_url_required'));
      return;
    }
    if (events.size === 0) {
      setError(t('wh_err_events_required'));
      return;
    }
    try {
      await onCreate({
        url: url.trim(),
        events: Array.from(events),
        description: description.trim() || undefined,
      });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message ?? t('wh_err_generic'));
      } else {
        setError(t('wh_err_generic'));
      }
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-ink/50 px-4"
      role="dialog"
      aria-modal="true"
      onClick={() => {
        if (!busy) onClose();
      }}
    >
      <form
        className="bg-surface border border-line rounded-lg shadow-lg w-full max-w-[520px] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        onSubmit={onSubmit}
      >
        <div className="px-5 py-4 border-b border-line">
          <h2 className="display text-[16px] font-semibold text-text">{t('wh_new_title')}</h2>
          <p className="text-[12px] text-text-3 mt-0.5">{t('wh_new_sub')}</p>
        </div>
        <div className="px-5 py-4 space-y-4">
          <div>
            <label className="block text-[12px] font-medium text-text-2 mb-1.5" htmlFor="wh-url">
              {t('wh_field_url')}
            </label>
            <Input
              id="wh-url"
              type="url"
              placeholder="https://example.com/webhooks/ezsender"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              disabled={busy}
              required
            />
          </div>
          <div>
            <label className="block text-[12px] font-medium text-text-2 mb-1.5" htmlFor="wh-desc">
              {t('wh_field_description')}
            </label>
            <Input
              id="wh-desc"
              placeholder={t('wh_field_description_placeholder')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={busy}
            />
          </div>
          <div>
            <span className="block text-[12px] font-medium text-text-2 mb-1.5">
              {t('wh_field_events')}
            </span>
            <div className="flex flex-col gap-2">
              {EVENT_TYPES.map((ev) => (
                <label
                  key={ev}
                  className={cn(
                    'flex items-center gap-2.5 px-3 py-2 rounded-md border text-[13px] cursor-pointer',
                    events.has(ev)
                      ? 'bg-volt-50 border-volt text-text'
                      : 'bg-surface border-line text-text-2 hover:bg-surface-2',
                  )}
                >
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={events.has(ev)}
                    onChange={() => toggle(ev)}
                    disabled={busy}
                  />
                  <span
                    aria-hidden="true"
                    className={cn(
                      'w-4 h-4 grid place-items-center rounded-xs border',
                      events.has(ev) ? 'bg-volt border-volt-600' : 'bg-surface border-line-strong',
                    )}
                  >
                    {events.has(ev) && (
                      <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                        <path
                          d="M2.5 6.5L4.8 8.8L9.5 3.5"
                          stroke="var(--volt-ink)"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </span>
                  <span className="mono text-[12px]">{ev}</span>
                </label>
              ))}
            </div>
          </div>
          {error && <p className="text-[12px] text-danger">{error}</p>}
        </div>
        <div className="px-5 py-4 border-t border-line flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
            {t('ac_cancel')}
          </Button>
          <Button type="submit" disabled={busy}>
            {busy ? t('wh_creating') : t('wh_create')}
          </Button>
        </div>
      </form>
    </div>
  );
}
